import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CountryFlag } from "@/lib/identity";
import { Trophy, X } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * ChampionshipInvitePopup — shows on Home when the logged-in user has a
 * pending championship invite. Persists dismissal in localStorage so it
 * doesn't harass the user on every navigation, but re-shows on new invites.
 */
const DISMISS_KEY = "nk_champ_invite_dismissed_v1";

export default function ChampionshipInvitePopup() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [responding, setResponding] = useState(false);

  useEffect(() => {
    api.get("/championship/my-status")
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, []);

  const invite = status?.invite;
  const showFor = invite && invite.status === "pending";
  const dismissKey = showFor ? `${DISMISS_KEY}:${invite.champ_id}` : null;

  useEffect(() => {
    if (!dismissKey) { setDismissed(false); return; }
    setDismissed(localStorage.getItem(dismissKey) === "1");
  }, [dismissKey]);

  if (!showFor || dismissed) return null;

  const respond = async (kind) => {
    setResponding(true);
    try {
      await api.post(`/championship/invite/${kind}`, { champ_id: invite.champ_id });
      toast.success(kind === "accept" ? "🎉 Seat locked in — see you Day 31" : "Declined — you can still watch");
      // Refresh
      const { data } = await api.get("/championship/my-status");
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setResponding(false); }
  };

  const dismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, "1");
    setDismissed(true);
  };

  const country = status.qualifier?.country || invite.country;
  const countryName = country === "GH" ? "Ghana" : country === "NG" ? "Nigeria" : country;
  const scheduled = invite.scheduled_at;
  const scheduledDate = scheduled ? new Date(scheduled) : null;

  return (
    <div
      data-testid="invite-popup"
      className="fixed inset-x-4 bottom-4 md:inset-x-auto md:right-6 md:bottom-6 md:w-96 z-40
                 nk-card p-5 border-nk-gold shadow-2xl bg-gradient-to-br from-nk-gold/[0.08] to-nk-charcoal
                 animate-in slide-in-from-bottom-4">
      <button
        onClick={dismiss}
        data-testid="invite-popup-dismiss"
        className="absolute top-2 right-2 p-1 text-nk-mute hover:text-nk-text">
        <X size={14} />
      </button>
      <div className="flex items-start gap-3">
        <Trophy size={28} className="text-nk-gold flex-shrink-0 mt-0.5" weight="fill" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-wider text-nk-gold mb-1">
            Championship invitation
          </p>
          <h3 className="font-display text-lg leading-tight">
            You&apos;ve qualified for Day 31 · Seed #{invite.seed}
          </h3>
          <p className="text-xs text-nk-mute mt-1 flex items-center gap-1">
            <CountryFlag country={country} size={11} /> Playing for {countryName}
          </p>
          {scheduledDate && (
            <div className="mt-2 p-2 rounded-sm border border-nk-line bg-nk-charcoal/50">
              <p className="text-[9px] font-mono uppercase text-nk-dim">Kickoff</p>
              <p className="text-sm text-nk-gold font-mono">
                {scheduledDate.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
                {" · "}
                {scheduledDate.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
              </p>
              <p className="text-[10px] text-nk-mute mt-0.5">
                {new Intl.DateTimeFormat(undefined, {
                  hour: "2-digit", minute: "2-digit", timeZone: "Africa/Accra",
                }).format(scheduledDate)} in Accra ·{" "}
                {new Intl.DateTimeFormat(undefined, {
                  hour: "2-digit", minute: "2-digit", timeZone: "Africa/Lagos",
                }).format(scheduledDate)} in Lagos
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button
          onClick={() => respond("accept")}
          disabled={responding}
          data-testid="invite-popup-accept"
          className="flex-1 px-3 py-2 rounded-sm bg-nk-gold text-nk-charcoal font-medium text-sm hover:brightness-110 transition-all disabled:opacity-50">
          Accept my seat
        </button>
        <button
          onClick={() => respond("decline")}
          disabled={responding}
          data-testid="invite-popup-decline"
          className="px-3 py-2 rounded-sm border border-nk-line text-sm hover:border-nk-bad hover:text-nk-bad transition-colors disabled:opacity-50">
          Decline
        </button>
      </div>
    </div>
  );
}
